import { Link } from 'gatsby'
import React, { HTMLAttributes } from 'react'

import { Container } from './Container'
import { LinkButton } from './LinkButton'
import { OpeningHours } from './OpeningHours'
import { SocialNetworks } from './SocialNetworks'
import { useContactInfo } from '../hooks/useContactInfo'
import { useOpeningHours } from '../hooks/useOpeningHours'
import { IconEmail, IconPhone } from './Icons'
import { IsOfficeOpen } from './IsOfficeOpen'

export interface FooterProps extends HTMLAttributes<HTMLElement> {
  onCookieShowPreferencesClick?: () => void
}

export interface FooterBottomProps extends HTMLAttributes<HTMLElement> {
  onCookieShowPreferencesClick?: () => void
}

export interface FooterColumnProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  columnTitle?: string | React.ReactNode
  rightElement?: React.ReactNode | undefined
}

const FooterBottom = ({ onCookieShowPreferencesClick }: FooterBottomProps) => {
  return (
    <div className="py-4 md:py-2 bg-primary-800 font-light text-sm text-white">
      <Container>
        <div className="grid gap-4 justify-items-center md:flex md:justify-between">
          <p className="order-1 md:order-0 ">
            Copyright © 2022-{new Date().getFullYear()}. Vytvořili{' '}
            <a
              className="underline"
              href="https://ctyri.media/"
              target="_blank"
            >
              čtyři.media
            </a>
            {' & '}
            <a
              className="underline"
              href="https://www.dvamluvci.cz/"
              target="_blank"
            >
              Dva Mluvčí
            </a>
          </p>

          <ul className="flex gap-x-4 gap-y-2">
            <li>
              <Link
                className="underline"
                to="/zasady-zpracovani-osobnich-udaju"
              >
                Zásady zpracování osobních údajů
              </Link>
            </li>
            <li>
              <a
                className="underline"
                href=""
                onClick={(e) => {
                  e.preventDefault()
                  onCookieShowPreferencesClick && onCookieShowPreferencesClick()
                }}
              >
                Nastavení cookies
              </a>
            </li>
          </ul>
        </div>
      </Container>
    </div>
  )
}

const FooterColumn = ({
  children,
  columnTitle,
  rightElement = null,
  className,
}: FooterColumnProps) => {
  return (
    <div className={`grid gap-4 justify-items-start ${className}`}>
      {columnTitle && typeof columnTitle === 'string' ? (
        <div className="flex w-full items-center justify-between">
          <h2 className="font-semibold text-secondary">{columnTitle}</h2>

          {rightElement && rightElement}
        </div>
      ) : (
        columnTitle
      )}

      {children}
    </div>
  )
}

export const Footer = ({ onCookieShowPreferencesClick }: FooterProps) => {
  const { openingHours } = useOpeningHours()
  const { basicContact, companyInfo, address, socialNetworks } =
    useContactInfo()
  const { email, phone } = basicContact!

  return (
    <footer className=" bg-primary">
      <div className="py-8">
        <Container>
          <div className="grid gap-8 items-start grid-cols-1 md:grid-cols-2 lg:grid-cols-4 text-white">
            <FooterColumn columnTitle={companyInfo?.companyName}>
              <ul>
                <li>IČ: {companyInfo?.businessId}</li>
                <li>DIČ: {companyInfo?.taxId}</li>
              </ul>

              <p className="text-xs">{companyInfo?.registryNotice}</p>
            </FooterColumn>

            <FooterColumn columnTitle="Sídlo a kancelář">
              <ul>
                <li>{address?.address1}</li>
                {address?.address2 && <li>{address?.address2}</li>}
                <li>
                  {address?.postalCode} {address?.city}
                </li>
                {address?.navigationLink && (
                  <li>
                    <a
                      className="underline"
                      href={address?.navigationLink}
                      target="_blank"
                    >
                      Navigovat
                    </a>
                  </li>
                )}
              </ul>
            </FooterColumn>

            <FooterColumn
              columnTitle="Provozní doba"
              rightElement={<IsOfficeOpen />}
            >
              {openingHours?.days && <OpeningHours days={openingHours.days} />}
            </FooterColumn>

            <FooterColumn className="grid gap-8 w-full justify-items-stretch sm:justify-items-start lg:justify-items-end">
              <div className="grid gap-1">
                {phone && (
                  <LinkButton href={`tel:+420${phone.replace(/\ /gi, '')}`}>
                    <span className="grid w-[29px] justify-center justify-self-start">
                      <IconPhone />
                    </span>
                    {`+420 ${phone}`}
                  </LinkButton>
                )}
                {email && (
                  <LinkButton href={`mailto:${email}`}>
                    <IconEmail />
                    {email}
                  </LinkButton>
                )}
              </div>

              {socialNetworks && socialNetworks.length && (
                <SocialNetworks networks={socialNetworks} iconColor="#D7B368" />
              )}
            </FooterColumn>
          </div>
        </Container>
      </div>

      <FooterBottom
        onCookieShowPreferencesClick={onCookieShowPreferencesClick}
      />
    </footer>
  )
}
